// Shopify bootstrap for the kit configurator theme section (theme-only setup, no Extend app).
// Sets window.KIT_ENV for app.js, then loads it. Everything Shopify-specific lives here:
//  - the three configurators' data come from theme assets (the same JSON the standalone demo uses);
//    the product page's handle picks which one opens, and switching product opens that product's page;
//  - image paths map to Shopify Files uploaded with the "mgt-" prefix, resized by the CDN to the width
//    each slot needs (the CDN also serves WebP to browsers that take it);
//  - each part's SKU code comes from kit-catalog.js and is matched to the live variant (id, price) of
//    the part products created by shopify/catalog.mjs;
//  - "Add kit to cart" adds the kit's variant as the parent line and one child line per priced part
//    (parent_id), so the cart groups them as a bundle. The visitor stays on the page: the theme's cart
//    icon is updated through its cart:update event and the button reads "Added to cart" for a while. Choices with no product of their own (tint shades,
//    coilover use case, installer shop) and $0 parts (keep-stock, standard fitment) become line notes;
//  - "Order materials" (Installer view) adds the job sheet's film rolls and coatings by SKU;
//  - the Installer view is offered to B2B buyers only (data-b2b, from customer.b2b? in Liquid);
//  - prices load alongside the first render rather than before it (pricesReady re-renders the build).

import { KIT_PRODUCTS, MATERIAL_PRODUCTS, PARTS, partSku } from './kit-catalog.js?v=4d617f7fc0';

const root = document.querySelector('[data-kit-configurator]');
const cfg = root.dataset;
const base = cfg.root || '/';
const dataUrls = JSON.parse(cfg.dataUrls);
const productId = Object.keys(KIT_PRODUCTS).find((id) => KIT_PRODUCTS[id].handle === cfg.productHandle) ?? Object.keys(KIT_PRODUCTS)[0];
const parts = PARTS[productId];

const variants = new Map(); // SKU code -> { id, price (cents), available, part }
const filesBase = cfg.filesBase.replace(/[^/]*$/, ''); // from `'x' | file_url`, minus the file name
const money = (cents) => new Intl.NumberFormat(cfg.locale || undefined, { style: 'currency', currency: cfg.currency || 'USD' }).format(cents / 100);

const getJson = async (url) => {
  const res = await fetch(url, { headers: { Accept: 'application/json' } });
  if (!res.ok) throw new Error(`${url}: HTTP ${res.status}`);
  return res.json();
};

/** One line per part whose options are all picked (optional parts the visitor skipped are left out). */
function partLines(selections) {
  return parts.flatMap((part) => {
    const sku = partSku(part, selections);
    return sku ? [{ part, sku, variant: variants.get(sku) }] : [];
  });
}

const label = (model, key, id) => model.attrByKey.get(key)?.values.find((v) => v.id === id)?.label ?? String(id);

/**
 * The cart lines for a build: priced parts as child lines, plus notes. A choice that has no product of
 * its own goes on the line of the part it belongs to (front shade -> tint line), else on the kit line;
 * a $0 part is noted on the kit line instead of getting a line (skipped when it is all "None").
 */
function cartLines(model, selections) {
  const lines = partLines(selections);
  const covered = new Set(parts.flatMap((p) => p.keys));
  const kitNotes = {};
  const notes = new Map(lines.map((l) => [l, {}]));
  for (const attr of model.attributes) {
    if (covered.has(attr.key) || selections[attr.key] === undefined) continue;
    const group = attr.key.split('.')[0];
    const home = lines.find((l) => l.variant?.price > 0 && l.part.keys.some((k) => k.startsWith(`${group}.`)));
    (home ? notes.get(home) : kitNotes)[attr.name] = label(model, attr.key, selections[attr.key]);
  }
  const priced = [];
  for (const l of lines) {
    if (l.variant?.price === 0) {
      if (!l.part.keys.every((k) => selections[k] === 'none')) {
        kitNotes[l.part.name] = l.part.keys.map((k) => label(model, k, selections[k])).join(' · ');
      }
    } else {
      priced.push({ ...l, notes: notes.get(l) });
    }
  }
  return { priced, kitNotes };
}

async function addItems(items) {
  const res = await fetch(`${base}cart/add.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ items }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.description || body.message || `HTTP ${res.status}`);
  }
  // Same event the theme's own product form sends; its cart icon reads data.itemCount.
  const cart = await getJson(`${base}cart.js`).catch(() => null);
  if (cart) {
    document.dispatchEvent(new CustomEvent('cart:update', {
      bubbles: true,
      detail: { resource: cart, sourceId: 'kit-configurator', data: { source: 'kit-configurator', itemCount: cart.item_count } },
    }));
  }
}

// Variant ids and prices of the part products, fetched while the configurator renders.
// A part product that isn't on sale yet (Draft) just leaves its options unpriced.
let pricesLoaded = false;
const pricesReady = Promise.all(parts.map(async (part) => {
  try {
    const product = await getJson(`${base}products/${part.handle}.js`);
    for (const v of product.variants) {
      if (v.sku) variants.set(v.sku, { id: v.id, price: v.price, available: v.available, part: part.name });
    }
  } catch (err) {
    console.warn(`Kit configurator: ${part.handle} not available`, err.message);
  }
})).then(() => { pricesLoaded = true; });

window.KIT_ENV = {
  setTitle: false,
  look: 'dark',
  productId,
  productUrl: (id) => `${base}products/${KIT_PRODUCTS[id].handle}`,
  installerView: cfg.b2b === 'true',
  productSwitch: false,
  pricesReady,

  loadProducts: () => Promise.all(Object.values(dataUrls).map(getJson)),

  // assets/views/car/base.jpg -> <files>/mgt-views-car-base.jpg?width=1200
  assetUrl: (path, width) => `${filesBase}mgt-${path.replace(/^assets\//, '').replaceAll('/', '-')}${width ? `?width=${width}` : ''}`,

  ctaNote(model, selections, complete) {
    const lines = partLines(selections);
    if (!lines.length) return null;
    if (!pricesLoaded) return 'Loading prices…';
    const missing = lines.filter((l) => !l.variant);
    if (missing.length) return `Not available to order yet: ${missing.map((l) => l.part.name).join(', ')}`;
    const total = money(lines.reduce((sum, l) => sum + l.variant.price, 0));
    return complete
      ? `Total ${total} · ${lines.length} ${lines.length === 1 ? 'part' : 'parts'}, all compatible.`
      : `${total} so far · ${lines.length} ${lines.length === 1 ? 'part' : 'parts'} chosen.`;
  },

  async addToCart(model, selections) {
    await pricesReady;
    const lines = partLines(selections);
    const missing = lines.filter((l) => !l.variant);
    if (!lines.length || missing.length) {
      throw new Error(missing.length ? `Not available to order yet: ${missing.map((l) => l.part.name).join(', ')}.` : 'Finish the build first.');
    }
    const unavailable = lines.filter((l) => !l.variant.available);
    if (unavailable.length) throw new Error(`Out of stock: ${unavailable.map((l) => l.variant.part).join(', ')}.`);

    const { priced, kitNotes } = cartLines(model, selections);
    const parent = Number(cfg.kitVariantId);
    await addItems([
      { id: parent, quantity: 1, properties: kitNotes },
      ...priced.map((l) => ({ id: l.variant.id, quantity: 1, parent_id: parent, properties: l.notes })),
    ]);
  },

  async orderMaterials(materials) {
    const bySku = new Map();
    await Promise.all(MATERIAL_PRODUCTS.map(async (handle) => {
      try {
        const product = await getJson(`${base}products/${handle}.js`);
        for (const v of product.variants) bySku.set(v.sku, v);
      } catch (err) {
        console.warn(`Kit configurator: ${handle} not available`, err.message);
      }
    }));
    const missing = materials.filter((m) => !bySku.get(m.sku)?.available);
    if (missing.length) throw new Error(`Not available to order: ${missing.map((m) => m.name).join(', ')}.`);
    await addItems(materials.map((m) => ({ id: bySku.get(m.sku).id, quantity: m.qty })));
  },
};

await import('./kit-configurator.js?v=eebd0e0599');
